import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import userService from "../../../services/userService";
import AuthService from "../../../services/authService";

const DEFAULT_FORM = {
  name: "",
  firstname: "",
  lastname: "",
  company: "",
  email: "",
  password: "",
};

const RegisterAccountModal = ({ onClose, onSuccess, onError, inputSx }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const fetchPermits = async () => {
      try {
        const response = await userService.getPermits();
        setPermissions(response.data);
      } catch (error) {
        console.error("Error al obtener permisos:", error);
      }
    };
    fetchPermits();
  }, []);

  const handleClose = () => {
    if (submitting) return;
    setErrors({});
    setForm(DEFAULT_FORM);
    onClose?.();
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.firstname.trim()) nextErrors.firstname = "El nombre es obligatorio";
    if (!form.lastname.trim()) nextErrors.lastname = "El apellido es obligatorio";
    if (!form.company.trim()) nextErrors.company = "La empresa es obligatoria";
    if (!form.email.trim()) nextErrors.email = "El email es obligatorio";
    if (!form.password.trim()) nextErrors.password = "La contraseña es obligatoria";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const email = form.email.trim();
      const payload = {
        // El email es la identidad: el username es el propio email.
        name: email,
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        password: form.password,
        active: true,
        is_owner: true,
        is_seller: false,
        permissions: permissions.map((p) => ({ id: p.id })),
        // Nombre real del negocio elegido por el usuario (antes iba un
        // placeholder "VORAS" que el backend ignoraba). El email de contacto
        // de la empresa es el del propio owner.
        company: { name: form.company.trim(), email },
      };

      // 1. Crear el usuario en Supabase Auth con la contraseña elegida. Esto
      //    dispara el mail de "Confirm sign up"; hasta confirmarlo no hay sesión.
      await AuthService.signUp({ email, password: form.password });

      // 2. Persistir el usuario (owner + company + permisos) en MySQL. El backend,
      //    al venir sin token (auto-registro), NO invita en Supabase: ya lo creó
      //    el signUp de arriba.
      await userService.createUser(payload);

      // El padre redirige a /login con el aviso de éxito; no hace falta resetear
      // ni cerrar acá (la navegación desmonta este formulario).
      onSuccess?.();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "No se pudo crear la cuenta. Verificá los datos o intentá más tarde.";
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = inputSx ? { ...inputSx, mb: 2 } : { mb: 2 };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleRegister();
      }}
      noValidate
      sx={{ display: "flex", flexDirection: "column", width: "100%" }}
    >
      <Stack spacing={0}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Nombre"
            value={form.firstname}
            onChange={(e) => setForm((prev) => ({ ...prev, firstname: e.target.value }))}
            error={Boolean(errors.firstname)}
            helperText={errors.firstname}
            required
            fullWidth
            size="small"
            sx={inputSx}
            autoComplete="given-name"
          />
          <TextField
            label="Apellido"
            value={form.lastname}
            onChange={(e) => setForm((prev) => ({ ...prev, lastname: e.target.value }))}
            error={Boolean(errors.lastname)}
            helperText={errors.lastname}
            required
            fullWidth
            size="small"
            sx={inputSx}
            autoComplete="family-name"
          />
        </Stack>
        <TextField
          label="Empresa"
          value={form.company}
          onChange={(e) => setForm((prev) => ({ ...prev, company: e.target.value }))}
          error={Boolean(errors.company)}
          helperText={errors.company || "El nombre de tu negocio o marca"}
          required
          fullWidth
          size="small"
          sx={fieldSx}
          autoComplete="organization"
        />
        <TextField
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          error={Boolean(errors.email)}
          helperText={errors.email}
          required
          fullWidth
          size="small"
          sx={fieldSx}
          autoComplete="email"
        />
        <TextField
          label="Contraseña"
          type="password"
          value={form.password}
          onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
          error={Boolean(errors.password)}
          helperText={errors.password}
          required
          fullWidth
          size="small"
          sx={{ ...fieldSx, mb: 3 }}
          autoComplete="new-password"
        />
      </Stack>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        disableElevation
        sx={{
          borderRadius: "12px",
          padding: "12px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          backgroundColor: "#2563EB",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "#1D4ED8",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0, 82, 204, 0.3)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
          "&.Mui-disabled": {
            backgroundColor: "#b3c9e8",
          },
          mb: 3,
        }}
      >
        {submitting ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          "Crear cuenta"
        )}
      </Button>

      {/* Aceptación de términos (clickwrap): requerido para el cobro por Stripe y
          la validez de los T&C ante disputas. */}
      <Box sx={{ textAlign: "center", mb: 2, mt: -1 }}>
        <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
          Al crear tu cuenta aceptás los{" "}
          <a href="/terminos" target="_blank" rel="noreferrer" style={{ color: "#2563EB" }}>
            Términos y Condiciones
          </a>{" "}
          y la{" "}
          <a href="/privacidad" target="_blank" rel="noreferrer" style={{ color: "#2563EB" }}>
            Política de Privacidad
          </a>
          .
        </span>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography variant="body2" sx={{ color: "#888", fontSize: "0.85rem" }}>
          ¿Ya tenés cuenta?{" "}
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClose();
              }
            }}
            onClick={handleClose}
            sx={{
              color: "#2563EB",
              fontWeight: 600,
              cursor: submitting ? "default" : "pointer",
              pointerEvents: submitting ? "none" : "auto",
              transition: "color 0.2s",
              "&:hover": { color: "#1D4ED8", textDecoration: "underline" },
            }}
          >
            Iniciar sesión
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterAccountModal;
